import React, { useEffect, useState } from 'react';
import { Tag } from 'antd';
import { gql, useQuery } from '@apollo/client';
import { isUndefined } from 'lodash';
import {
  EntityTypeEnum,
  PersonSelectionRowContainerQuery, PersonSelectionRowContainerQueryVariables,
  PersonSelectionRowCriteria,
  SlicerSelectionType,
} from '../../../../gql/typings';
import EntityTable from '../../../components/entitiesSearch/results/EntityTable';
import { useEntityTableData } from '../../../components/entitiesSearch/results/useEntityTableData';
import { useLocalization } from '../../../util/useLocalization';
import { Locale } from '../../../../localization/LocalizationKeys';
import { useSystemPermission } from '../../../permission/useSystemPermission';

type PersonSelectionRowsContainerProps = {
  personId: number;
  apurebaseId: number;
};

const PersonSelectionRowsContainer: React.FC<PersonSelectionRowsContainerProps> = ({ apurebaseId }) => {
  const localization = useLocalization();
  const [showAll, setShowAll] = useState(false);
  const [selectedCustomerCodes, setSelectedCustomerCodes] = useState<string[]>();
  const hasGlobalSearchPermission = useSystemPermission('globalSearch');
  const entityTableProps = useEntityTableData<PersonSelectionRowCriteria>(
    EntityTypeEnum.PERS_SELECTION_ROW,
    {
      aPureBaseId: apurebaseId,
      selectionType: showAll ? null : SlicerSelectionType.DYNAMIC,
      imfCustomerCodes: selectedCustomerCodes,
    },
    hasGlobalSearchPermission
  );
  const { data, loading } = useQuery<PersonSelectionRowContainerQuery, PersonSelectionRowContainerQueryVariables>(DATA_QUERY, {
    variables: {
      personApureBaseID: apurebaseId,
      selectionType: showAll ? null : SlicerSelectionType.DYNAMIC,
    },
  });
  useEffect(() => {
    if (isUndefined(selectedCustomerCodes) && data?.selectionImfCustomers) {
      setSelectedCustomerCodes(data.selectionImfCustomers.nodes.map(n => n.code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div>
      <EntityTable
        {...entityTableProps}
        loading={loading}
        actionsToTake={[
          {
            key: 'show-selection-types',
            label: showAll
              ? localization.formatMessage(Locale.Command.Only_show_dynamic_selection_types)
              : localization.formatMessage(Locale.Command.Show_all_selection_types),
            onClick: () => setShowAll(c => !c),
          }
        ]}
        quickActions={[
          ...(data?.selectionImfCustomers.nodes ?? []).map(customer => (
            <Tag.CheckableTag
              key={customer.code}
              checked={selectedCustomerCodes?.includes(customer.code) ?? false}
              onChange={checked => {
                if (checked) {
                  setSelectedCustomerCodes([...selectedCustomerCodes ?? [], customer.code]);
                } else {
                  setSelectedCustomerCodes(selectedCustomerCodes?.filter(c => c !== customer.code));
                }
              }}
            >
              {customer.callName ?? customer.name}
            </Tag.CheckableTag>
          )),
        ]}
      />
    </div>
  );
};

const DATA_QUERY = gql`
  query PersonSelectionRowContainer($personApureBaseID: Int!, $selectionType: SlicerSelectionType) {
    selectionImfCustomers(criteria: {
      personApbIdInSelection: [$personApureBaseID],
      selectionType: $selectionType
    }) {
      hash
      nodes {
        code
        callName
        name
      }
    }
  }
`;

export default PersonSelectionRowsContainer;
